import { useEffect, useMemo, useState } from 'react';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Select, Switch, TextInput } from '../../../components/Field';
import { Modal } from '../../../overlay';
import { toast } from '../../../components/Toast';
import { api, errorMessage } from '../../../lib/api';
import type { Drone, DroneGroup } from '@shared/types';

interface BatchConfigModalProps {
  open: boolean;
  drones: readonly Drone[];
  groups: readonly DroneGroup[];
  onClose: () => void;
  onApplied: (updated: Drone[]) => void;
}

interface DiffRow {
  droneId: string;
  name: string;
  changes: { field: string; from: string; to: string }[];
}

const KEEP = '__keep__';
const NONE = '__none__';

/**
 * 批量统一配置。
 *
 * 写操作前先把「旧值 → 新值」的差异列出来，避免多选之后误改一整批设备。
 */
export function BatchConfigModal({ open, drones, groups, onClose, onApplied }: BatchConfigModalProps) {
  const [enabledValue, setEnabledValue] = useState(KEEP);
  const [groupIdValue, setGroupIdValue] = useState(KEEP);
  const [tagsText, setTagsText] = useState('');
  const [tagMode, setTagMode] = useState<'add' | 'remove' | 'replace'>('add');
  const [rateText, setRateText] = useState('');
  const [staleText, setStaleText] = useState('');
  const [rotateCredential, setRotateCredential] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEnabledValue(KEEP);
    setGroupIdValue(KEEP);
    setTagsText('');
    setTagMode('add');
    setRateText('');
    setStaleText('');
    setRotateCredential(false);
  }, [open]);

  const tags = useMemo(
    () =>
      tagsText
        .split(/[,，\s]+/)
        .map((tag) => tag.trim())
        .filter((tag) => tag !== ''),
    [tagsText],
  );

  const diff = useMemo<DiffRow[]>(() => {
    const groupName = (id: string | null) =>
      id === null ? '未分组' : groups.find((group) => group.id === id)?.name ?? id;

    return drones.map((drone) => {
      const changes: DiffRow['changes'] = [];
      if (enabledValue !== KEEP) {
        const next = enabledValue === 'on';
        if (next !== drone.enabled) {
          changes.push({
            field: '启用状态',
            from: drone.enabled ? '启用' : '停用',
            to: next ? '启用' : '停用',
          });
        }
      }
      if (groupIdValue !== KEEP) {
        const next = groupIdValue === NONE ? null : groupIdValue;
        if (next !== drone.groupId) {
          changes.push({ field: '分组', from: groupName(drone.groupId), to: groupName(next) });
        }
      }
      if (tags.length > 0) {
        const next =
          tagMode === 'replace'
            ? Array.from(new Set(tags))
            : tagMode === 'add'
              ? Array.from(new Set([...drone.tags, ...tags]))
              : drone.tags.filter((tag) => !tags.includes(tag));
        if (next.join(',') !== drone.tags.join(',')) {
          changes.push({ field: '标签', from: drone.tags.join(' ') || '（空）', to: next.join(' ') || '（空）' });
        }
      }
      if (rateText.trim() !== '') {
        const next = Number(rateText);
        if (Number.isFinite(next) && next !== drone.telemetryRateHz) {
          changes.push({
            field: '遥测频率',
            from: `${drone.telemetryRateHz} Hz`,
            to: `${next} Hz`,
          });
        }
      }
      if (staleText.trim() !== '') {
        const next = Number(staleText);
        if (Number.isFinite(next) && next !== drone.staleAfterMs) {
          changes.push({
            field: '失联阈值',
            from: `${drone.staleAfterMs} ms`,
            to: `${next} ms`,
          });
        }
      }
      if (rotateCredential) {
        changes.push({ field: '接入凭据', from: '现有凭据', to: '重新签发' });
      }
      return { droneId: drone.id, name: drone.name || drone.uniqueId, changes };
    });
  }, [drones, groups, enabledValue, groupIdValue, tags, tagMode, rateText, staleText, rotateCredential]);

  const touched = diff.filter((row) => row.changes.length > 0);
  const nothingToDo = touched.length === 0;

  async function apply() {
    if (nothingToDo) return;
    const body: Record<string, unknown> = { ids: drones.map((drone) => drone.id) };
    if (enabledValue !== KEEP) body.enabled = enabledValue === 'on';
    if (groupIdValue !== KEEP) body.groupId = groupIdValue === NONE ? null : groupIdValue;
    if (tags.length > 0) {
      body.tags = tags;
      body.tagMode = tagMode;
    }
    if (rateText.trim() !== '') body.telemetryRateHz = Number(rateText);
    if (staleText.trim() !== '') body.staleAfterMs = Number(staleText);
    if (rotateCredential) body.rotateCredential = true;

    setApplying(true);
    try {
      const result = await api.post<{ updated: Drone[]; skipped: string[] }>(
        '/api/drones/batch',
        body,
      );
      toast.ok(
        `已更新 ${result.updated.length} 台无人机${result.skipped.length > 0 ? `，跳过 ${result.skipped.length} 台` : ''}`,
      );
      onApplied(result.updated);
      onClose();
    } catch (error) {
      toast.critical(errorMessage(error));
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={`统一配置 ${drones.length} 台无人机`}
      description="未填写或选择「保持不变」的项目不会被修改；提交前请核对下方差异。"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={applying}>
            取消
          </Button>
          <Button
            variant="primary"
            loading={applying}
            disabled={nothingToDo}
            onClick={() => void apply()}
          >
            应用修改（{touched.length} 台）
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="启用状态"
            value={enabledValue}
            options={[
              { value: KEEP, label: '保持不变' },
              { value: 'on', label: '全部启用' },
              { value: 'off', label: '全部停用' },
            ]}
            onChange={(event) => setEnabledValue(event.target.value)}
          />
          <Select
            label="分组"
            value={groupIdValue}
            options={[
              { value: KEEP, label: '保持不变' },
              { value: NONE, label: '移出分组' },
              ...groups.map((group) => ({ value: group.id, label: group.name })),
            ]}
            onChange={(event) => setGroupIdValue(event.target.value)}
          />
          <TextInput
            label="遥测频率（Hz，留空不变）"
            inputMode="decimal"
            value={rateText}
            onChange={(event) => setRateText(event.target.value)}
          />
          <TextInput
            label="失联判定阈值（ms，留空不变）"
            inputMode="numeric"
            value={staleText}
            onChange={(event) => setStaleText(event.target.value)}
          />
          <TextInput
            label="标签"
            value={tagsText}
            hint="多个标签用逗号分隔"
            onChange={(event) => setTagsText(event.target.value)}
          />
          <Select
            label="标签处理方式"
            value={tagMode}
            options={[
              { value: 'add', label: '追加到现有标签' },
              { value: 'remove', label: '从现有标签中移除' },
              { value: 'replace', label: '整体替换现有标签' },
            ]}
            onChange={(event) => setTagMode(event.target.value as 'add' | 'remove' | 'replace')}
          />
        </div>

        <Switch
          checked={rotateCredential}
          label="同时轮换接入凭据"
          hint="仅对 platformToken 模式生效；apiKey 模式需要逐台填写新密钥，请在单机编辑中处理。"
          onChange={setRotateCredential}
        />

        <div className="rounded-md border border-line">
          <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-2 px-3 py-2">
            <span className="text-xs font-semibold text-fg-muted">变更预览</span>
            <Badge tone={nothingToDo ? 'neutral' : 'info'}>
              {nothingToDo ? '暂无改动' : `${touched.length} / ${drones.length} 台将被修改`}
            </Badge>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {nothingToDo ? (
              <p className="px-3 py-6 text-center text-xs text-fg-subtle">
                选择要统一修改的项目后，这里会列出每台设备的旧值与新值。
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {touched.map((row) => (
                  <li key={row.droneId} className="px-3 py-2">
                    <div className="text-sm font-medium text-fg">{row.name}</div>
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {row.changes.map((change) => (
                        <li key={change.field} className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span className="text-fg-muted">{change.field}</span>
                          <span className="text-fg-subtle line-through">{change.from}</span>
                          <span aria-hidden className="text-fg-subtle">
                            →
                          </span>
                          <span className="font-medium text-accent">{change.to}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
